import { motion } from "framer-motion";
import { ArrowRight, CheckCircle2, Zap, TrendingUp } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { FadeUp } from "@/components/Reveal";

const STAGES = [
    { name: "New", color: "bg-sky-400", deals: ["Om infra LLP", "Kaveri Foods", "Sintech"] },
    { name: "Qualified", color: "bg-royal", deals: ["Delta Pipes", "Arjun Steels"] },
    { name: "Proposal", color: "bg-amberct", deals: ["Nexus Build"] },
    { name: "Won", color: "bg-emerald-500", deals: ["Peak Infra"] },
];

const PipelineMock = () => (
    <motion.div
        initial={{ opacity: 0, y: 40, rotate: 1.5 }}
        animate={{ opacity: 1, y: 0, rotate: 0 }}
        transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        className="relative"
        data-testid="hero-pipeline-mock"
        aria-hidden="true"
    >
        <div className="rounded-2xl border border-slate-200 bg-white shadow-2xl shadow-royal/10">
            <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3.5">
                <span className="h-2.5 w-2.5 rounded-full bg-red-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amberct" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
                <span className="ml-3 font-mono text-xs text-slate-400">pipeline.yourcompany.in</span>
            </div>
            <div className="grid grid-cols-4 gap-3 p-5">
                {STAGES.map((stage) => (
                    <div key={stage.name}>
                        <div className="mb-2.5 flex items-center gap-1.5">
                            <span className={`h-2 w-2 rounded-full ${stage.color}`} />
                            <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{stage.name}</span>
                        </div>
                        <div className="space-y-2">
                            {stage.deals.map((deal) => (
                                <div key={deal} className="rounded-lg border border-slate-100 bg-slate-50 px-2.5 py-2">
                                    <p className="truncate text-[11px] font-semibold text-navy">{deal}</p>
                                    <p className="mt-0.5 text-[10px] text-slate-400">₹4.2L · 2d</p>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
            </div>
            <div className="flex items-end justify-between border-t border-slate-100 px-5 py-4">
                <div className="flex items-end gap-1.5" aria-hidden="true">
                    {[35, 55, 42, 70, 58, 88, 76, 100].map((h, i) => (
                        <motion.span
                            key={i}
                            initial={{ height: 0 }}
                            animate={{ height: `${h * 0.4}px` }}
                            transition={{ delay: 0.8 + i * 0.08, duration: 0.5, ease: "easeOut" }}
                            className={`w-4 rounded-t ${i === 7 ? "bg-amberct" : "bg-royal/80"}`}
                        />
                    ))}
                </div>
                <div className="text-right">
                    <p className="text-[10px] uppercase tracking-wider text-slate-400">Pipeline value</p>
                    <p className="font-display text-lg font-extrabold text-navy">₹1.24 Cr</p>
                </div>
            </div>
        </div>

        <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.1, duration: 0.6 }}
            className="absolute -left-4 top-16 hidden items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg sm:flex lg:-left-10"
        >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50">
                <Zap className="h-4 w-4 text-emerald-500" />
            </span>
            <div>
                <p className="text-xs font-bold text-navy">Follow-up sent automatically</p>
                <p className="text-[11px] text-slate-400">2 minutes ago · no rep needed</p>
            </div>
        </motion.div>

        <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 1.3, duration: 0.6 }}
            className="absolute -bottom-5 right-4 flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white px-4 py-3 shadow-lg"
        >
            <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-50">
                <TrendingUp className="h-4 w-4 text-royal" />
            </span>
            <div>
                <p className="text-xs font-bold text-navy">40% faster follow-up</p>
                <p className="text-[11px] text-slate-400">this quarter vs last</p>
            </div>
        </motion.div>
    </motion.div>
);

const Hero = () => {
    const { content } = useSite();
    const { hero } = content;

    return (
        <section id="top" data-testid="hero-section" className="relative overflow-hidden bg-mist">
            <div className="hero-grid-bg absolute inset-0" aria-hidden="true" />
            <div className="absolute -top-32 left-1/2 h-96 w-[42rem] -translate-x-1/2 rounded-full bg-royal/10 blur-3xl" aria-hidden="true" />

            <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 pb-24 pt-16 sm:px-6 lg:grid-cols-2 lg:px-8 lg:pb-32 lg:pt-24">
                <div>
                    <FadeUp>
                        <p className="inline-flex items-center gap-2 rounded-full border border-royal/20 bg-white px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-royal shadow-sm">
                            <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                            {hero.eyebrow}
                        </p>
                    </FadeUp>

                    <FadeUp delay={0.1}>
                        <h1 className="mt-6 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
                            {hero.headline}
                        </h1>
                    </FadeUp>

                    <FadeUp delay={0.2}>
                        <p className="mt-6 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">{hero.subhead}</p>
                    </FadeUp>

                    <FadeUp delay={0.3} className="mt-8 flex flex-col gap-3 sm:flex-row">
                        <a
                            href="#contact"
                            data-testid="hero-book-call-button"
                            className="inline-flex items-center justify-center gap-2 rounded-xl bg-amberct px-7 py-4 text-base font-bold text-navy shadow-lg shadow-amberct/25 transition-all hover:-translate-y-0.5 hover:bg-amber-400 hover:shadow-xl"
                        >
                            {hero.primaryCta} <ArrowRight className="h-5 w-5" />
                        </a>
                        <a
                            href="#how-it-works"
                            data-testid="hero-how-it-works-button"
                            className="inline-flex items-center justify-center gap-2 rounded-xl border-2 border-navy/10 bg-white px-7 py-4 text-base font-bold text-navy transition-all hover:-translate-y-0.5 hover:border-royal/30 hover:text-royal"
                        >
                            {hero.secondaryCta}
                        </a>
                    </FadeUp>

                    <FadeUp delay={0.4}>
                        <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
                            {hero.microTrust.map((item) => (
                                <li key={item} className="flex items-center gap-2 text-sm font-medium text-slate-500">
                                    <CheckCircle2 className="h-4 w-4 text-emerald-500" /> {item}
                                </li>
                            ))}
                        </ul>
                    </FadeUp>
                </div>

                <div className="lg:pl-6">
                    <PipelineMock />
                </div>
            </div>
        </section>
    );
};

export default Hero;
